// Holiday API service — Laravel apiResource: /api/holidays

import { apiRequest } from './api';

// ── API record types ────────────────────────────────────────

export type HolidayType = 'public' | 'academic_break' | 'restricted' | 'emergency';
export type HolidayAppliesTo = 'all' | 'students_only' | 'staff_only';

export type HolidayApiRecord = {
    id: string;
    organization_id: string;
    school_id: string;
    academic_session_id: string;
    name: string;
    type: HolidayType;
    applies_to: HolidayAppliesTo;
    start_date: string;
    end_date: string;
    description: string | null;
    created_at: string;
    updated_at: string;
};

export type HolidayPayload = {
    school_id: string;
    academic_session_id: string;
    name: string;
    type: HolidayType;
    applies_to: HolidayAppliesTo;
    start_date: string;
    end_date: string;
    description?: string | null;
};

export type HolidayListResponse = {
    success: boolean;
    message: string;
    data: HolidayApiRecord[];
    meta: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
    };
};

// ── API Functions ───────────────────────────────────────────

/**
 * List holidays with optional pagination.
 */
export async function fetchHolidays(params?: {
    page?: number;
    perPage?: number;
    schoolId?: string;
    academicSessionId?: string;
}): Promise<HolidayListResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.perPage) query.set('per_page', String(params.perPage));
    if (params?.schoolId) query.set('school_id', params.schoolId);
    if (params?.academicSessionId) query.set('academic_session_id', params.academicSessionId);

    const qs = query.toString();
    const res = await apiRequest<HolidayApiRecord[]>(`/api/holidays${qs ? `?${qs}` : ''}`);

    return {
        success: res.success,
        message: res.message,
        data: res.data,
        meta: (res as unknown as HolidayListResponse).meta,
    };
}

/**
 * Create a new holiday.
 */
export async function createHoliday(payload: HolidayPayload): Promise<HolidayApiRecord> {
    const res = await apiRequest<HolidayApiRecord>('/api/holidays', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}

/**
 * Update an existing holiday.
 */
export async function updateHoliday(id: string, payload: HolidayPayload): Promise<HolidayApiRecord> {
    const res = await apiRequest<HolidayApiRecord>(`/api/holidays/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    return res.data;
}

/**
 * Delete a holiday.
 */
export async function deleteHoliday(id: string): Promise<void> {
    await apiRequest(`/api/holidays/${id}`, { method: 'DELETE' });
}
